import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CreatePlanDto } from '@workspace/data-types';
import { DialogLayoutAuraComponent } from '@workspace/ng-aura';
import { ApiService } from '@workspace/ng-http';
import { catchError, finalize, of, tap } from 'rxjs';

@Component({
  selector: 'core-plan-create-dialog',
  templateUrl: './plan-create-dialog.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    DialogLayoutAuraComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanCreateDialogCoreComponent {
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly dialogRef = inject(MatDialogRef<PlanCreateDialogCoreComponent>);

  isLoading = signal<boolean>(false);
  errorMessage = signal<string | null>(null);

  availableFeatures = [
    { value: 'properties:create', label: 'Créer des biens (limité/starter)' },
    { value: 'properties:unlimited', label: 'Biens illimités (pro)' },
    { value: 'documents:scan', label: 'Scanner des documents' },
    { value: 'analytics:view', label: 'Voir les statistiques/analyses' },
  ];

  form = this.fb.group({
    name: ['', [Validators.required]],
    slug: ['', [Validators.required, Validators.pattern(/^[a-z0-9-]+$/)]],
    price: [0, [Validators.required, Validators.min(0)]],
    billingCycle: ['monthly', [Validators.required]],
    isActive: [true, [Validators.required]],
    features: [['properties:create'], [Validators.required]],
    stripeProductId: [''],
  });

  onSubmit(): void {
    if (this.form.invalid) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const formVal = this.form.value;

    const dto: CreatePlanDto = {
      name: formVal.name ?? '',
      slug: formVal.slug ?? '',
      price: Math.round((formVal.price ?? 0) * 100), // convert EUR to cents for Stripe
      billingCycle: formVal.billingCycle ?? 'monthly',
      currency: 'eur',
      isActive: formVal.isActive ?? true,
      features: (formVal.features as string[]) ?? [],
      stripeProductId: formVal.stripeProductId?.trim() || undefined,
    };

    this.api
      .admin()
      .plans()
      .create(dto)
      .pipe(
        tap((createdPlan) => {
          this.dialogRef.close(createdPlan);
        }),
        catchError((err) => {
          this.errorMessage.set(
            err?.error?.message || err?.message || 'Une erreur est survenue lors de la création du plan',
          );
          return of(null);
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe();
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
